// src/store/slices/syncSlice.ts
// 🔄 Синхронизация с бэкендом

import type { StateCreator } from 'zustand';
import type { RecommendProduct } from '../../services/api';
import { apiClient, normalizeDates } from '../../services/api';

// ========== SLICE STATE ==========

export interface SyncSlice {
  // State
  recommendedProducts: RecommendProduct[];
  lastSync: string | null;
  useRetinolBeforeBackend: boolean | null;
  
  // Actions
  initializeFromBackend: (
    dates: string[],
    products?: RecommendProduct[],
    planStart?: string | null,
  ) => Promise<void>; // ✅ async
  syncFromBackend: () => Promise<void>;
  resetSync: () => void;
}

// ========== INITIAL STATE ==========

const initialSyncState: Pick<SyncSlice, 'recommendedProducts' | 'lastSync' | 'useRetinolBeforeBackend'> = {
  recommendedProducts:[],
  lastSync: null,
  useRetinolBeforeBackend: null,
};

// ========== SLICE CREATOR ==========

export const createSyncSlice: StateCreator<SyncSlice,[],[], SyncSlice> = (set, get) => ({
  ...initialSyncState,

  // ✅ Инициализация из бэкенда: сначала календарь, затем продукты
    initializeFromBackend: async (dates, products =[], planStart) => {
    // Обновляем календарь (если слайс календаря уже включён в стор)
    const fullState = get() as any;
    if (typeof fullState.initializeCalendar === 'function') {
      await fullState.initializeCalendar(dates, planStart); // ✅ await для сверки дат
    }

    // Сохраняем продукты и метку времени синка
    set({
      recommendedProducts: products,
      lastSync: new Date().toISOString(),
    });

    if (process.env.NODE_ENV === 'development') {
      console.log('✅ Данные с бэкенда инициализированы:', {
        dates: dates.length,
        products: products.length,
        productNames: products.map((p: RecommendProduct) => p.product_name),
      });
    }
  },

  // ✅ Полная синхронизация с сервера
  syncFromBackend: async () => {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('🔄 Синхронизация календаря...');
      }
      const userInfo = await apiClient.getUserInfo();

      const normalizedDates = userInfo.dates ? normalizeDates(userInfo.dates) :[];
      const planStart = userInfo.plan_start_use_retinol || null;
      const products: RecommendProduct[] = userInfo.recommend_products ||[];
      const useRetinolBefore =
        typeof userInfo.use_retinol_before === 'boolean' ? userInfo.use_retinol_before : null;

      set({
        recommendedProducts: products,
        lastSync: new Date().toISOString(),
        useRetinolBeforeBackend: useRetinolBefore,
      });

      // Обновляем календарь напрямую
      const fullState = get() as any;
      if (typeof fullState.initializeCalendar === 'function') {
        await fullState.initializeCalendar(normalizedDates, planStart); // ✅ await
      }

      if (process.env.NODE_ENV === 'development') {
        console.log('✅ Календарь синхронизирован:', {
          dates: normalizedDates.length,
          products: products.length,
        });
      }
    } catch (error: unknown) {
      // Task 5: Обработка 404 - пользователь не найден
      if ((error as any)?.status === 404) {
        console.error('❌ Пользователь не найден (404). Требуется заполнить анкету.');
        // Сбрасываем store
        const fullState = get() as any;
        if (typeof fullState.reset === 'function') {
          fullState.reset();
        }
        // Создаем ошибку с флагами для редиректа
        const notFoundError = new Error('USER_NOT_FOUND');
        (notFoundError as any).shouldRedirect = true;
        (notFoundError as any).redirectTo = '/questionnaire';
        throw notFoundError;
      }

      const errorMessage = error instanceof Error ? error.message : 'Неизвестная ошибка';
      console.error('❌ Ошибка синхронизации:', errorMessage);
      throw error;
    }
  },

  resetSync: () => {
    set(initialSyncState);
  },
});
